<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { onMount } from 'svelte';
  import Header from '$lib/components/sections/Header.svelte';
  import Footer from '$lib/components/sections/Footer.svelte';

  let emailLink: HTMLAnchorElement | null = null;

  onMount(() => {
    if (!emailLink) return;
    const u = 'hello';
    const d = 'heyweek' + '.com';
    emailLink.href = 'mailto:' + u + '@' + d;
    emailLink.textContent = u + '@' + d;
  });
</script>

<svelte:head>
  <title>Contact Heyweek — Support &amp; Sales</title>
  <meta name="description" content="Get in touch with the Heyweek team — questions, support, sales, or feedback. We read every message and usually reply within one business day." />
	<meta property="og:title" content="Contact — Heyweek" />
	<meta property="og:description" content="Get in touch with the Heyweek team — questions, support, sales, or feedback. We read every message and usually reply within one business day." />
  <link rel="canonical" href="https://heyweek.com/contact" />
</svelte:head>

<Header />

<main class="page">

  <!-- Hero -->
  <section class="hero">
    <div class="container">
      <p class="hero__eyebrow">{$_('pages.contactPage.hero.eyebrow')}</p>
      <h1 class="hero__title">{$_('pages.contactPage.hero.title')}</h1>
      <p class="hero__subtitle">{$_('pages.contactPage.hero.subtitle')}</p>
    </div>
  </section>

  <!-- Channels -->
  <section class="channels">
    <div class="container">
      <div class="channels__grid">

        <a href="/docs" class="channel">
          <div class="channel__icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
              <path d="M12 17h.01"/>
            </svg>
          </div>
          <div class="channel__body">
            <h2 class="channel__title">{$_('pages.contactPage.channels.helpDocs.title')}</h2>
            <p class="channel__desc">{$_('pages.contactPage.channels.helpDocs.desc')}</p>
          </div>
          <div class="channel__arrow" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="5" y1="12" x2="19" y2="12"/>
              <polyline points="12 5 19 12 12 19"/>
            </svg>
          </div>
        </a>

        <a href="/faq" class="channel">
          <div class="channel__icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
              <path d="M12 17h.01"/>
            </svg>
          </div>
          <div class="channel__body">
            <h2 class="channel__title">{$_('pages.contactPage.channels.faq.title')}</h2>
            <p class="channel__desc">{$_('pages.contactPage.channels.faq.desc')}</p>
          </div>
          <div class="channel__arrow" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="5" y1="12" x2="19" y2="12"/>
              <polyline points="12 5 19 12 12 19"/>
            </svg>
          </div>
        </a>

        <a href="/press-kit" class="channel">
          <div class="channel__icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
              <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2Zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2"/>
              <path d="M18 14h-8"/>
              <path d="M15 18h-5"/>
              <path d="M10 6h8v4h-8V6Z"/>
            </svg>
          </div>
          <div class="channel__body">
            <h2 class="channel__title">{$_('pages.contactPage.channels.press.title')}</h2>
            <p class="channel__desc">{$_('pages.contactPage.channels.press.desc')}</p>
          </div>
          <div class="channel__arrow" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="5" y1="12" x2="19" y2="12"/>
              <polyline points="12 5 19 12 12 19"/>
            </svg>
          </div>
        </a>

      </div>

      <!-- Direct email -->
      <div class="direct">
        <p class="direct__label">{$_('pages.contactPage.direct.label')}</p>
        <a bind:this={emailLink} class="direct__link" aria-label={$_('pages.contactPage.direct.emailAriaLabel')}>&#8203;</a>
        <p class="direct__note">{$_('pages.contactPage.direct.note')}</p>
      </div>

    </div>
  </section>

</main>

<Footer />

<style>
  .page {
    min-height: 100vh;
  }

  .container {
    max-width: 760px;
    margin-inline: auto;
    padding-inline: var(--container-padding, 1.5rem);
  }

  /* ── Hero ── */
  .hero {
    padding-block: var(--space-20) var(--space-16);
    border-bottom: 1px solid var(--border-light);
  }

  .hero__eyebrow {
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    color: var(--text-secondary);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    margin-bottom: var(--space-5);
  }

  .hero__title {
    font-size: clamp(3rem, 7vw, 5rem);
    font-weight: var(--font-weight-bold);
    line-height: 1.05;
    letter-spacing: -0.03em;
    color: var(--text-primary);
    margin-bottom: var(--space-5);
  }

  .hero__subtitle {
    font-size: var(--font-size-lg);
    color: var(--text-secondary);
    line-height: 1.7;
    max-width: 480px;
  }

  /* ── Channels ── */
  .channels {
    padding-block: var(--space-16);
  }

  .channels__grid {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .channel {
    display: flex;
    align-items: center;
    gap: var(--space-5);
    padding: var(--space-6) var(--space-7);
    background: var(--bg-secondary);
    border: 1px solid var(--border-light);
    border-radius: var(--radius-xl);
    text-decoration: none;
    transition: box-shadow 0.2s ease;
  }

  .channel:hover {
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  }

  .channel:hover .channel__arrow {
    opacity: 1;
    transform: translateX(2px);
  }

  .channel__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: var(--radius-md);
    background: var(--bg-primary);
    border: 1px solid var(--border-light);
    color: var(--text-secondary);
    flex-shrink: 0;
  }

  .channel__body {
    flex: 1;
  }

  .channel__title {
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    letter-spacing: -0.01em;
    margin-bottom: var(--space-1);
  }

  .channel__desc {
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
    line-height: 1.6;
    margin-bottom: var(--space-2);
  }

  .channel__email {
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    color: var(--text-brand);
  }

  .channel__inline-link {
    color: var(--text-primary);
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  .channel__inline-link:hover {
    color: var(--text-brand);
  }

  .channel__arrow {
    color: var(--text-secondary);
    opacity: 0;
    transition: opacity 0.2s ease, transform 0.2s ease;
    flex-shrink: 0;
  }

  /* ── Direct email ── */
  .direct {
    margin-top: var(--space-16);
    padding-top: var(--space-10);
    border-top: 1px solid var(--border-light);
  }

  .direct__label {
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    color: var(--text-secondary);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    margin-bottom: var(--space-3);
  }

  .direct__link {
    display: inline-block;
    font-size: var(--font-size-xl);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    text-decoration: none;
    letter-spacing: -0.01em;
    margin-bottom: var(--space-3);
  }

  .direct__link:hover {
    color: var(--text-brand);
  }

  .direct__note {
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
    line-height: 1.6;
  }

  @media (max-width: 640px) {
    .channel {
      padding: var(--space-5);
      gap: var(--space-4);
    }

    .channel__arrow {
      display: none;
    }
  }
</style>
